import * as React from "react";
import { Sparkles } from "lucide-react";

/**
 * The product's AI marker: the SAME lucide `Sparkles` used everywhere else
 * (answer-generation panel, fact rows, AI-identified controls), with its stroke
 * overridden by the Lytra brand gradient — purple -> cyan -> yellow, the
 * logo-icon stops, in the order DESIGN.md fixes.
 *
 * DESIGN.md: "Use a colorful gradient to indicate ai generated content to
 * visualize the 'magic'." Reach for this rather than a flat purple sparkle
 * whenever a control is marked as AI-derived.
 *
 * Extracted from SourceReview.tsx (#433) so the AI-identified Button variant
 * and the source rows share one marker instead of two that can drift (#455).
 *
 * NOTE: the three stops are written as literal hex rather than
 * `var(--gradient-from|via|to)`, which hold exactly these values. The literals
 * are what this component shipped with; switching to the tokens resolves fine
 * in both apps (theme.css defines them on `:root, :host`) but is a behaviour
 * change to a component already live in the answer panel, so it belongs in its
 * own change rather than riding along with the extraction.
 */
export interface GradientSparkleProps {
  className?: string;
  /**
   * Render the same sparkle path as a solid `currentColor` fill instead of the
   * brand gradient. Deliberate exception to the DESIGN.md gradient rule, used
   * where Figma specifies a flat sparkle (the AI badge variants, #455 step 4).
   * Everywhere else the gradient remains the AI marker.
   */
  solid?: boolean;
}

export function GradientSparkle({ className, solid = false }: GradientSparkleProps) {
  // React.useId() returns an id containing colons (":r0:"), which does NOT
  // resolve inside an SVG url(#...) reference — strip them so the gradient
  // actually applies (otherwise the stroke paints as nothing).
  const gid = "sparkle-" + React.useId().replace(/:/g, "");
  if (solid) {
    return (
      <Sparkles className={className} fill="currentColor" stroke="none" aria-hidden="true" />
    );
  }
  return (
    <>
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#D571E5" />
            <stop offset="0.5" stopColor="#80E4FB" />
            <stop offset="1" stopColor="#F5F886" />
          </linearGradient>
        </defs>
      </svg>
      <Sparkles className={className} style={{ stroke: `url(#${gid})` }} aria-hidden="true" />
    </>
  );
}
